import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Select, { MultiValue } from "react-select";

import TasksPageTable from "./TasksPageTable";

// ... (Keep all imports the same)
import TaskList from "../../../interfaces/task/TaskList";
import { User } from "../../../interfaces/User";
import Participant from "../../../interfaces/Participant";
import Status from "../../../interfaces/task/Status";
import Avatar from "../../../interfaces/avatar/Avatar";
import filtericon from "../../../assets/images/FilterIcons.svg";
import Shorting from "../../../assets/images/Shorting.svg";
import downloaddropdownicon from "../../../assets/images/downloaddropdownicon.svg";
import Button from "react-bootstrap/Button";
import Dropdown from "react-bootstrap/Dropdown";
import Offcanvas from "react-bootstrap/Offcanvas";
import Tag from "../../../interfaces/tag/Tag";
import "./TasksPage.css";
import { calculateDateProximity } from "../../../utilities/dateUtils";
import { HiMiniPlusCircle } from "react-icons/hi2";
import ModalMeet from "../calendar/ModalMeet";
import LogActivityModal from "./LogActivityModal";
import NewTask from "./NewTask";
import AvatarFilter from "../ui/AvatarFilter";
import "../ui/AvatarFilter.css";
import { useAuth } from "../../../firebase/AuthProvider";
import TagSelector from "./TagSelector";
import AvatarGroupTask from "./AvatarGroupTask";

// ... (Keep Interfaces and Constants the same)

interface TasksPageProps {
  tasks: TaskList[];
  assignees: User[];
  tags: Tag[];
  participants: Participant[];
  statuses: Status[];
  avatars: Avatar[];
  currentPage: number;
  pageSize: number;
  totalTasks: number;
  onPageChange: (n: number) => void;
  isLoading: boolean;
  onFilterChange: (filters: {
    tags: string[];
    status?: string[];
    type?: string[];
    date?: string[];
    assignees?: string[];
    participants?: string[];
    viewAll?: boolean;
    searchTerm?: string;
    excludeDate?: string[];
    excludeStatus?: string[];
    excludeType?: string[];
    includePendingApprovals?: boolean;
    userId?: string;
  }) => void;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  handleSort: (columnName: string, direction?: boolean) => void;
  onPdfDownload: () => void;
  totalTasksCount: number;
  isPdfDownloading: boolean;
}

interface OptionType {
  value: string;
  label: string;
}

export interface AssigneeOption {
  value: string;
  label: string;
  image?: string;
}

export interface IncludeExcludeAssignee {
  include: AssigneeOption[];
  exclude: AssigneeOption[];
}

const PlusCircle = HiMiniPlusCircle as any;

const EXCLUDED_STATUSES = ['DELETED', 'COMPLETED', 'CLOSED'];

const sortOptions = [
  { label: "Task Name (A-Z)", column: "name", direction: false },
  { label: "Task Name (Z-A)", column: "name", direction: true },
  { label: "Due date (Earliest → Latest)", column: "enddate", direction: false },
  { label: "Due date (Latest → Earliest)", column: "enddate", direction: true },
  { label: "Last activity (Recent first)", column: "lastActivityDate", direction: true },
  { label: "Created date (Newest first)", column: "startDate", direction: true },
  { label: "Created date (Oldest first)", column: "startDate", direction: false },
];

const TasksPageNew: React.FC<TasksPageProps> = ({
  tasks,
  assignees,
  tags,
  participants,
  statuses,
  avatars,
  currentPage,
  totalTasks,
  pageSize,
  onPageChange,
  onFilterChange,
  isLoading,
  setPage,
  setPageSize,
  handleSort,
  onPdfDownload,
  totalTasksCount,
  isPdfDownloading,
}) => {
  // ... (Keep state hooks the same)
  const [show, setShow] = useState(false);
  const handleClose = () => setShow(false);
  const { currentRoles } = useAuth();
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  useEffect(() => {
    const checkFeedbackOpen = () => {
      setFeedbackOpen(document.body.classList.contains('feedback-open'));
    };

    checkFeedbackOpen();

    const observer = new MutationObserver(checkFeedbackOpen);
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });

    return () => observer.disconnect();
  }, []);

  const isOnlySC =
    Array.isArray(currentRoles) &&
    currentRoles.length > 0 &&
    currentRoles.every((role) => role.name === "SC");

  const [assigneeFilter, setAssigneeFilter] = useState<MultiValue<OptionType>>([]);
  const [participantFilter, setParticipantFilter] = useState<MultiValue<OptionType>>([]);
  const [typeFilter, setTypeFilter] = useState<MultiValue<OptionType>>([]);
  const [excludeTypeFilter, setExcludeTypeFilter] = useState<MultiValue<OptionType>>([]);
  const [statusFilter, setStatusFilter] = useState<MultiValue<OptionType>>([]);
  const [excludeStatusFilter, setExcludeStatusFilter] = useState<MultiValue<OptionType>>([]);
  const [excludeTagsFilter, setExcludeTagsFilter] = useState<MultiValue<OptionType>>([]);
  const [tempExcludeTagsFilter, setTempExcludeTagsFilter] = useState<MultiValue<OptionType>>([]);
  const [dateFilter, setDateFilter] = useState<MultiValue<OptionType>>([]);
  const [excludeDateFilter, setExcludeDateFilter] = useState<MultiValue<OptionType>>([]);
  const [tagsFilter, setTagsFilter] = useState<MultiValue<OptionType>>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [assigneeIdFilter, setAssigneeIdFilter] = useState<string[]>([]);

  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: "asc" | "desc";
  } | null>(null);
  const [isSortDropdownOpen, setIsSortDropdownOpen] = useState(false);
  const [currentSortOption, setCurrentSortOption] = useState<string>("Task Name (A-Z)");
  const [sortDesc, setSortDesc] = useState<boolean>(false);
  const sortDropdownRef = useRef<HTMLDivElement>(null);

  const [tempAssigneeFilter, setTempAssigneeFilter] = useState(assigneeFilter);
  const [tempParticipantFilter, setTempParticipantFilter] = useState(participantFilter);
  const [tempTypeFilter, setTempTypeFilter] = useState(typeFilter);
  const [tempStatusFilter, setTempStatusFilter] = useState(statusFilter);
  const [tempDateFilter, setTempDateFilter] = useState(dateFilter);
  const [tempExcludeDateFilter, setTempExcludeDateFilter] = useState(excludeDateFilter);
  const [tempExcludeStatusFilter, setTempExcludeStatusFilter] = useState(excludeStatusFilter);
  const [tempExcludeTypeFilter, setTempExcludeTypeFilter] = useState(excludeTypeFilter);
  const [tempTagsFilter, setTempTagsFilter] = useState(tagsFilter);
  const [tempSearchTerm, setTempSearchTerm] = useState(searchTerm);
  const [isOpenActivity, setOpenActivity] = useState(false);
  const [isOpenNewTask, setOpenNewTask] = useState(false);

  // ... (Keep helper functions like isNumericSearch, handleShow, refs)

  const isNumericSearch = (value: string) => {
    const v = value.trim();
    return /^#?\d+$/.test(v);
  };

  const handleShow = () => {
    setTempAssigneeFilter(assigneeFilter);
    setTempParticipantFilter(participantFilter);
    setTempTypeFilter(typeFilter);
    setTempStatusFilter(statusFilter);
    setTempDateFilter(dateFilter);
    setTempExcludeDateFilter(excludeDateFilter);
    setTempExcludeStatusFilter(excludeStatusFilter);
    setTempExcludeTypeFilter(excludeTypeFilter);
    setTempTagsFilter(tagsFilter);
    setTempExcludeTagsFilter(excludeTagsFilter);
    setTempSearchTerm(searchTerm);
    setAssigneeSearchTerm("");
    setParticipantSearchTerm("");
    setShow(true);
    setToggleFilterForm(true);
  };

  const navigate = useNavigate();
  const location = useLocation();

  const [toggleFilterForm, setToggleFilterForm] = useState(false);
  const [viewAll, setViewAll] = useState(false);

  const assigneeDropdownRef = useRef<HTMLDivElement>(null);
  const assigneeSearchInputRef = useRef<HTMLInputElement>(null);
  const assigneeDropdownListRef = useRef<HTMLDivElement>(null);
  const assigneeOptionRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [assigneeDropdownOpen, setAssigneeDropdownOpen] = useState(false);
  const [assigneeSearchTerm, setAssigneeSearchTerm] = useState("");
  const [focusedAssigneeIndex, setFocusedAssigneeIndex] = useState<number>(-1);

  const participantDropdownRef = useRef<HTMLDivElement>(null);
  const participantSearchInputRef = useRef<HTMLInputElement>(null);
  const participantDropdownListRef = useRef<HTMLDivElement>(null);
  const participantOptionRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [participantDropdownOpen, setParticipantDropdownOpen] = useState(false);
  const [participantSearchTerm, setParticipantSearchTerm] = useState("");
  const [focusedParticipantIndex, setFocusedParticipantIndex] = useState<number>(-1);

  const tagsDropdownRef = useRef<HTMLDivElement>(null);
  const [tagsDropdownOpen, setTagsDropdownOpen] = useState(false);
  const [tagsSearchTerm, setTagsSearchTerm] = useState("");

  const tagsOptions: OptionType[] = useMemo(
    () =>
      tags.map((tag) => ({
        value: tag.name.toString(),
        label: tag.name,
      })),
    [tags]
  );

  const assigneeOptions: OptionType[] = useMemo(
    () =>
      assignees.map((assignee) => ({
        value: assignee.id.toString(),
        label: assignee.displayName,
      })),
    [assignees]
  );

  const filteredTasks = useMemo(() => {
    if (assigneeIdFilter.length === 0) {
      return tasks;
    }
    return tasks.filter((task) =>
      task.assignees.some((assignee) =>
        assigneeIdFilter.includes(assignee.user.id)
      )
    );
  }, [tasks, assigneeIdFilter]);

  const participantOptions: OptionType[] = useMemo(
    () => [
      ...participants.map((p) => ({
        value: p.id.toString(),
        label: `${p.firstName} ${p.lastName}`,
      })),
    ],
    [participants]
  );

  const statusOptions: OptionType[] = useMemo(
    () =>
      statuses
        .filter((status) => !EXCLUDED_STATUSES.includes(status.name))
        .map((status) => ({
          value: status.name,
          label: status.name,
        })),
    [statuses]
  );

  const dateOptions: OptionType[] = useMemo(
    () => [
      { value: "overdue", label: "Overdue" },
      { value: "today", label: "Today" },
      { value: "week", label: "This Week" },
      { value: "month", label: "This Month" },
    ],
    []
  );

  const typeOptions: OptionType[] = useMemo(
    () => [
      { value: "interim", label: "Interim" },
      { value: "monthly", label: "Monthly" },
      { value: "quarterly", label: "Quarterly" },
      { value: "annual", label: "Annual" },
      { value: "initial", label: "Initial" }, // <--- ADDED INITIAL
    ],
    []
  );

  // ... (Keep filtered options logic)
  const filteredStatusOptions = useMemo(() => {
    const excludedValues = tempExcludeStatusFilter.map((f) => f.value);
    return statusOptions.filter(
      (option) => !excludedValues.includes(option.value)
    );
  }, [statusOptions, tempExcludeStatusFilter]);

  const filteredExcludeStatusOptions = useMemo(() => {
    const excludedValues = tempStatusFilter.map((f) => f.value);
    return statusOptions.filter(
      (option) => !excludedValues.includes(option.value)
    );
  }, [statusOptions, tempStatusFilter]);

  const filteredTypeOptions = useMemo(() => {
    const excludedValues = tempExcludeTypeFilter.map((f) => f.value);
    return typeOptions.filter(
      (option) => !excludedValues.includes(option.value)
    );
  }, [typeOptions, tempExcludeTypeFilter]);

  const filteredExcludeTypeOptions = useMemo(() => {
    const excludedValues = tempTypeFilter.map((f) => f.value);
    return typeOptions.filter(
      (option) => !excludedValues.includes(option.value)
    );
  }, [typeOptions, tempTypeFilter]);

  // Combined Filters
  const taskTypeValue = useMemo(
    () => ({
      include: tempTypeFilter as OptionType[],
      exclude: tempExcludeTypeFilter as OptionType[],
    }),
    [tempTypeFilter, tempExcludeTypeFilter]
  );

  const taskStatusValue = useMemo(
    () => ({
      include: tempStatusFilter as OptionType[],
      exclude: tempExcludeStatusFilter as OptionType[],
    }),
    [tempStatusFilter, tempExcludeStatusFilter]
  );

  const taskTagsValue = useMemo(
    () => ({
      include: tempTagsFilter as OptionType[],
      exclude: tempExcludeTagsFilter as OptionType[],
    }),
    [tempTagsFilter, tempExcludeTagsFilter]
  );

  const taskDateValue = useMemo(
    () => ({
      include: tempDateFilter as OptionType[],
      exclude: tempExcludeDateFilter as OptionType[],
    }),
    [tempDateFilter, tempExcludeDateFilter]
  );

  const avatarAssignees = useMemo(() => {
    return assignees.map((a) => ({
      id: a.id,
      name: a.displayName,
      image: a.avatar || undefined,
    }));
  }, [assignees]);

  // ... (Keep handlers)
  const handleTaskTypeChange = (value: { include: OptionType[]; exclude: OptionType[] }) => {
    setTempTypeFilter(value.include);
    setTempExcludeTypeFilter(value.exclude);
  };

  const handleTaskStatusChange = (value: { include: OptionType[]; exclude: OptionType[] }) => {
    setTempStatusFilter(value.include);
    setTempExcludeStatusFilter(value.exclude);
  };

  const handleTaskTagsChange = (value: { include: OptionType[]; exclude: OptionType[] }) => {
    setTempTagsFilter(value.include);
    setTempExcludeTagsFilter(value.exclude);
  };

  const handleTaskDateChange = (value: { include: OptionType[]; exclude: OptionType[] }) => {
    setTempDateFilter(value.include);
    setTempExcludeDateFilter(value.exclude);
  };

  // Helper to safely parse URL params into Options, even if they aren't in the list yet
  const parseUrlParamToOptions = (
    paramValue: string | null,
    optionsList: OptionType[]
  ): OptionType[] => {
    if (!paramValue) return [];
    
    const values = paramValue.split(",").map(s => s.trim());
    
    return values.map(val => {
        // Try to find case-insensitive match
        const match = optionsList.find(opt => opt.value.toLowerCase() === val.toLowerCase());
        // Return match if found, otherwise Create a temporary option to ensure the filter isn't lost
        // (Useful if options haven't loaded yet or for hidden values)
        return match || { value: val, label: val }; 
    });
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);

    const viewAllParam = params.get("viewAll");
    setViewAll(viewAllParam === "true");

    const assignee = params.getAll("assignee").join(",") || null;
    const participant = params.getAll("participant").join(",") || null;
    const type = params.getAll("type").join(",") || null;
    const status = params.getAll("status").join(",") || null;
    const date = params.getAll("date").join(",") || null;
    const excludeDate = params.getAll("excludeDate").join(",") || null;
    const excludeStatus = params.getAll("excludeStatus").join(",") || null;
    const excludeType = params.getAll("excludeType").join(",") || null;
    const tags = params.get("tags");
    const excludeTags = params.get("excludeTags");
    const searchTermFromUrl = params.get("searchTerm") || "";
    const pageSizeFromUrl = params.get("pageSize");
    const includePendingApprovalsFromUrl = params.get("includePendingApprovals");
    const userIdFromUrl = params.get("userId");

    // Use the robust parsing helper
    const assigneeParsed = parseUrlParamToOptions(assignee, assigneeOptions);
    const participantParsed = parseUrlParamToOptions(participant, participantOptions);
    const typeParsed = parseUrlParamToOptions(type, typeOptions);
    const statusParsed = parseUrlParamToOptions(status, statusOptions);
    const dateParsed = parseUrlParamToOptions(date, dateOptions);
    const excludeDateParsed = parseUrlParamToOptions(excludeDate, dateOptions);
    const excludeStatusParsed = parseUrlParamToOptions(excludeStatus, statusOptions);
    const excludeTypeParsed = parseUrlParamToOptions(excludeType, typeOptions);
    const tagsParsed = parseUrlParamToOptions(tags, tagsOptions);
    const excludeTagsParsed = parseUrlParamToOptions(excludeTags, tagsOptions);

    setAssigneeFilter(assigneeParsed);
    setParticipantFilter(participantParsed);
    setTypeFilter(typeParsed);
    setStatusFilter(statusParsed);
    setDateFilter(dateParsed);
    setExcludeDateFilter(excludeDateParsed);
    setExcludeStatusFilter(excludeStatusParsed);
    setExcludeTypeFilter(excludeTypeParsed);
    setTagsFilter(tagsParsed);
    setSearchTerm(searchTermFromUrl);
    setExcludeTagsFilter(excludeTagsParsed);

    if (pageSizeFromUrl) {
      const parsedPageSize = parseInt(pageSizeFromUrl, 10);
      const validPageSizes = [10, 25, 50, 100];
      if (validPageSizes.includes(parsedPageSize)) {
        setPageSize(parsedPageSize);
      }
    }

    const filterUpdate = {
      assignees: assigneeParsed.map((it) => it.value),
      participants: participantParsed.map((it) => it.value),
      type: typeParsed.map((it) => it.value),
      status: statusParsed.map((it) => it.value),
      date: dateParsed.map((it) => it.value),
      excludeDate: excludeDateParsed.map((it) => it.value),
      excludeStatus: excludeStatusParsed.map((it) => it.value),
      excludeType: excludeTypeParsed.map((it) => it.value),
      tags: tagsParsed.map((it) => it.value),
      excludeTags: excludeTagsParsed.map((it) => it.value),
      viewAll: viewAllParam === "true",
      searchTerm: searchTermFromUrl,
      includePendingApprovals: includePendingApprovalsFromUrl === "true",
      userId: userIdFromUrl || undefined,
    };

    onFilterChange(filterUpdate);
  }, [
    location.search,
    // Note: We keep options in dependency array so if options load LATER, we re-parse
    // to potentially upgrade our "temporary" options to "real" options (with correct labels).
    assigneeOptions,
    participantOptions,
    typeOptions,
    statusOptions,
    dateOptions,
    tagsOptions,
    onFilterChange,
    setPageSize,
  ]);

  // ... (Keep applyFilters, handleAssigneeFilterChange, etc. and rest of the component)

  const applyFilters = () => {
    setAssigneeFilter(tempAssigneeFilter);
    setParticipantFilter(tempParticipantFilter);
    setTypeFilter(tempTypeFilter);
    setStatusFilter(tempStatusFilter);
    setDateFilter(tempDateFilter);
    setExcludeDateFilter(tempExcludeDateFilter);
    setExcludeStatusFilter(tempExcludeStatusFilter);
    setExcludeTypeFilter(tempExcludeTypeFilter);
    setTagsFilter(tempTagsFilter);
    setExcludeTagsFilter(tempExcludeTagsFilter);
    setSearchTerm(tempSearchTerm);

    const params = new URLSearchParams();
    if (tempSearchTerm) params.set("searchTerm", tempSearchTerm);
    if (tempAssigneeFilter.length > 0)
      params.set("assignee", tempAssigneeFilter.map((a) => a.value).join(","));
    if (tempParticipantFilter.length > 0)
      params.set(
        "participant",
        tempParticipantFilter.map((p) => p.value).join(",")
      );
    if (tempTypeFilter.length > 0)
      params.set("type", tempTypeFilter.map((t) => t.value).join(","));
    if (tempStatusFilter.length > 0)
      params.set("status", tempStatusFilter.map((s) => s.value).join(","));
    if (tempDateFilter.length > 0)
      params.set("date", tempDateFilter.map((d) => d.value).join(","));
    if (tempExcludeDateFilter.length > 0)
      params.set(
        "excludeDate",
        tempExcludeDateFilter.map((d) => d.value).join(",")
      );
    if (tempExcludeStatusFilter.length > 0)
      params.set(
        "excludeStatus",
        tempExcludeStatusFilter.map((s) => s.value).join(",")
      );
    if (tempExcludeTypeFilter.length > 0)
      params.set(
        "excludeType",
        tempExcludeTypeFilter.map((t) => t.value).join(",")
      );
    if (tempTagsFilter.length > 0)
      params.set("tags", tempTagsFilter.map((t) => t.value).join(","));

    if (tempExcludeTagsFilter.length > 0)
      params.set(
        "excludeTags",
        tempExcludeTagsFilter.map((t) => t.value).join(",")
      );
    if (viewAll) params.set("viewAll", "true");
    
    const savedPageSize = localStorage.getItem("tasksPageSize");
    const pageSizeToUse = savedPageSize
      ? parseInt(savedPageSize, 10)
      : pageSize;
    params.set("pageSize", pageSizeToUse.toString());

    navigate(`${location.pathname}?${params.toString()}`, {
      replace: true,
    });
    handleClose();
  };

  // ... (Keep remaining component logic unchanged)
  const handleAssigneeFilterChange = (newValue: MultiValue<OptionType>) =>
    setTempAssigneeFilter(newValue);
  const handleParticipantFilterChange = (newValue: MultiValue<OptionType>) =>
    setTempParticipantFilter(newValue);
  const handleTypeFilterChange = (newValue: MultiValue<OptionType>) => {
    setTempTypeFilter(newValue);
    const newTypeValues = newValue.map((v) => v.value);
    setTempExcludeTypeFilter((prev) =>
      prev.filter((item) => !newTypeValues.includes(item.value))
    );
  };
  const handleStatusFilterChange = (newValue: MultiValue<OptionType>) => {
    setTempStatusFilter(newValue);
    const newStatusValues = newValue.map((v) => v.value);
    setTempExcludeStatusFilter((prev) =>
      prev.filter((item) => !newStatusValues.includes(item.value))
    );
  };
  const handleDateFilterChange = (newValue: MultiValue<OptionType>) =>
    setTempDateFilter(newValue);
  const handleExcludeStatusFilterChange = (newValue: MultiValue<OptionType>) => {
    setTempExcludeStatusFilter(newValue);
    const newExcludeStatusValues = newValue.map((v) => v.value);
    setTempStatusFilter((prev) =>
      prev.filter((item) => !newExcludeStatusValues.includes(item.value))
    );
  };
  const handleExcludeTypeFilterChange = (newValue: MultiValue<OptionType>) => {
    setTempExcludeTypeFilter(newValue);
    const newExcludeTypeValues = newValue.map((v) => v.value);
    setTempTypeFilter((prev) =>
      prev.filter((item) => !newExcludeTypeValues.includes(item.value))
    );
  };

  const filteredAssigneeOptions = assigneeOptions.filter((a) =>
    a.label.toLowerCase().includes(assigneeSearchTerm.toLowerCase())
  );
  const toggleAssignee = (id: string) => {
    setTempAssigneeFilter((prev) => {
      const exists = prev.find((a) => a.value === id);
      if (exists) return prev.filter((a) => a.value !== id);
      const option = assigneeOptions.find((a) => a.value === id);
      return option ? [...prev, option] : prev;
    });
  };

  const createDropdownKeyHandler = (
    isOpen: boolean,
    options: OptionType[],
    focusedIndex: number,
    setFocusedIndex: (index: number | ((prev: number) => number)) => void,
    optionRefs: React.MutableRefObject<(HTMLDivElement | null)[]>,
    onToggle: (value: string) => void,
    onClose: () => void
  ) => {
    return (e: React.KeyboardEvent) => {
      if (!isOpen || options.length === 0) return;
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          e.stopPropagation();
          setFocusedIndex((prev) => {
            const nextIndex = prev < options.length - 1 ? prev + 1 : 0;
            setTimeout(() => {
              optionRefs.current[nextIndex]?.scrollIntoView({
                block: "nearest",
                behavior: "smooth",
              });
            }, 0);
            return nextIndex;
          });
          break;
        case "ArrowUp":
          e.preventDefault();
          e.stopPropagation();
          setFocusedIndex((prev) => {
            const nextIndex = prev > 0 ? prev - 1 : options.length - 1;
            setTimeout(() => {
              optionRefs.current[nextIndex]?.scrollIntoView({
                block: "nearest",
                behavior: "smooth",
              });
            }, 0);
            return nextIndex;
          });
          break;
        case "Enter":
          e.preventDefault();
          e.stopPropagation();
          if (focusedIndex >= 0 && focusedIndex < options.length) {
            onToggle(options[focusedIndex].value);
          }
          break;
        case "Escape":
          e.preventDefault();
          e.stopPropagation();
          onClose();
          setFocusedIndex(-1);
          break;
      }
    };
  };

  const isScrollbarClick = (e: MouseEvent) => {
    return (
      e.clientX >= document.documentElement.clientWidth ||
      e.clientY >= document.documentElement.clientHeight
    );
  };

  const handleAssigneeKeyDown = createDropdownKeyHandler(
    assigneeDropdownOpen,
    filteredAssigneeOptions,
    focusedAssigneeIndex,
    setFocusedAssigneeIndex,
    assigneeOptionRefs,
    toggleAssignee,
    () => setAssigneeDropdownOpen(false)
  );

  useEffect(() => {
    if (assigneeDropdownOpen && assigneeSearchInputRef.current) {
      setTimeout(() => {
        assigneeSearchInputRef.current?.focus();
      }, 0);
    }
    setFocusedAssigneeIndex(-1);
  }, [assigneeDropdownOpen]);

  useEffect(() => {
    setFocusedAssigneeIndex(-1);
  }, [assigneeSearchTerm]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        assigneeDropdownRef.current &&
        !assigneeDropdownRef.current.contains(event.target as Node)
      ) {
        setAssigneeDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        sortDropdownRef.current &&
        !sortDropdownRef.current.contains(event.target as Node)
      ) {
        setIsSortDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredParticipantOptions = participantOptions.filter((p) =>
    p.label.toLowerCase().includes(participantSearchTerm.toLowerCase())
  );
  const toggleParticipant = (value: string) => {
    const exists = tempParticipantFilter.some((f) => f.value === value);
    const selected = participantOptions.find((p) => p.value === value);
    if (!selected) return;
    if (exists) {
      setTempParticipantFilter(
        tempParticipantFilter.filter((f) => f.value !== value)
      );
    } else {
      setTempParticipantFilter([...tempParticipantFilter, selected]);
    }
  };

  const handleParticipantKeyDown = createDropdownKeyHandler(
    participantDropdownOpen,
    filteredParticipantOptions,
    focusedParticipantIndex,
    setFocusedParticipantIndex,
    participantOptionRefs,
    toggleParticipant,
    () => setParticipantDropdownOpen(false)
  );

  useEffect(() => {
    if (participantDropdownOpen && participantSearchInputRef.current) {
      setTimeout(() => {
        participantSearchInputRef.current?.focus();
      }, 0);
      setFocusedParticipantIndex(-1);
    } else {
      setFocusedParticipantIndex(-1);
    }
  }, [participantDropdownOpen]);

  useEffect(() => {
    setFocusedParticipantIndex(-1);
  }, [participantSearchTerm]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        participantDropdownRef.current &&
        !participantDropdownRef.current.contains(event.target as Node)
      ) {
        setParticipantDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredTagsOptions = tagsOptions.filter((t) =>
    t.label.toLowerCase().includes(tagsSearchTerm.toLowerCase())
  );
  const toggleTag = (id: string) => {
    setTempTagsFilter((prev) => {
      const exists = prev.find((t) => t.value === id);
      if (exists) return prev.filter((t) => t.value !== id);
      const option = tagsOptions.find((t) => t.value === id);
      return option ? [...prev, option] : prev;
    });
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isScrollbarClick(event)) return; 
      if (
        tagsDropdownRef.current &&
        !tagsDropdownRef.current.contains(event.target as Node)
      ) {
        setTagsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside, true);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside, true);
  }, []);

  useEffect(() => {
    localStorage.setItem("tasksPageSize", pageSize.toString());
    const params = new URLSearchParams(location.search);
    params.set("pageSize", pageSize.toString());
    navigate(`${location.pathname}?${params.toString()}`, {
      replace: true,
    });
  }, [pageSize, location.pathname, navigate]);

  const clearFilters = () => {
    setTempAssigneeFilter([]);
    setTempParticipantFilter([]);
    setTempTypeFilter([]);
    setTempStatusFilter([]);
    setTempDateFilter([]);
    setTempExcludeDateFilter([]);
    setTempExcludeStatusFilter([]);
    setTempExcludeTypeFilter([]);
    setTempTagsFilter([]);
    setTempExcludeTagsFilter([]);
    setTempSearchTerm("");
    handleClose();
    navigate("/tasks?pageSize=10");
  };

  const displayedTaskCount =
    assigneeIdFilter.length > 0 ? filteredTasks.length : totalTasksCount || 0;

  const handleTaskSort = (option: string) => {
    const sortOption = sortOptions.find(opt => opt.label === option);
    if (!sortOption) return;
    const { column, direction } = sortOption;
    setSortConfig({ key: column, direction: direction ? "desc" : "asc" });
    setCurrentSortOption(option);
    setIsSortDropdownOpen(false);
    const params = new URLSearchParams(location.search);
    params.set('sortBy', column);
    params.set('sortDesc', direction.toString());
    navigate({ search: params.toString() }, { replace: true });
    handleSort(column, direction);
  };

  const sortedTasks = useMemo(() => {
    if (!sortConfig) return filteredTasks;
    return [...filteredTasks].sort((a, b) => {
      let aValue: any, bValue: any;
      switch (sortConfig.key) {
        case "title":
          aValue = a.title || "";
          bValue = b.title || "";
          if (sortConfig.direction === "asc") return aValue.localeCompare(bValue);
          else return bValue.localeCompare(aValue);
        case "endDate":
          aValue = a.endDate ? new Date(a.endDate) : new Date(0);
          bValue = b.endDate ? new Date(b.endDate) : new Date(0);
          if (sortConfig.direction === "asc") return aValue.getTime() - bValue.getTime();
          else return bValue.getTime() - aValue.getTime();
        case "lastActivityDate":
          aValue = a.lastActivityDate ? new Date(a.lastActivityDate) : new Date(0);
          bValue = b.lastActivityDate ? new Date(b.lastActivityDate) : new Date(0);
          if (sortConfig.direction === "desc") return bValue.getTime() - aValue.getTime();
          else return aValue.getTime() - bValue.getTime();
        case "startDate":
          aValue = a.startDate ? new Date(a.startDate) : new Date(0);
          bValue = b.startDate ? new Date(b.startDate) : new Date(0);
          if (sortConfig.direction === "desc") return bValue.getTime() - aValue.getTime();
          else return aValue.getTime() - bValue.getTime();
        default:
          aValue = a.title || "";
          bValue = b.title || "";
          if (sortConfig.direction === "asc") return aValue.localeCompare(bValue);
          else return bValue.localeCompare(aValue);
      }
    });
  }, [filteredTasks, sortConfig]);

  useEffect(() => {
    if (!assigneeIdFilter) return;
    const params = new URLSearchParams(location.search);
    if (assigneeIdFilter.length > 0) {
      params.set("assignee", assigneeIdFilter.join(","));
    } else {
      params.delete("assignee");
    }
    params.set("pageSize", pageSize.toString());
    navigate(`${location.pathname}?${params.toString()}`, {
      replace: true,
    });
  }, [assigneeIdFilter]);

  return (
    <div className="tasks_page_container shadow-sm h-full">
      <div className="w-full flex flex-wrap justify-between items-center gap-y-2 px-2">
        <div className="flex items-center gap-2.5">
          <h3 className="text-lg">
            All Tasks{" "}
            <span className="text-[#747474] !text-xs">
              {" "}
              ({totalTasksCount})
            </span>
          </h3>
          <button
            onClick={onPdfDownload}
            className="flex text-[#747474] items-center gap-2 border border-[#C9C9C9] bg-white px-4 py-2 rounded-[10px] hover:bg-gray-100 text-xs font-medium"
          >
            <span>Download</span>
            <img
              src={downloaddropdownicon}
              alt="viewdropdownicon"
              style={{ width: "17px" }}
            />
          </button>
          <div className="relative" ref={sortDropdownRef}>
            <button
              className="flex text-[#747474] items-center gap-2 border border-[#C9C9C9] bg-white px-4 py-2 rounded-[10px] hover:bg-gray-100 text-xs font-medium"
              onClick={() => setIsSortDropdownOpen(!isSortDropdownOpen)}
            >
              <span>Sort</span>
              <img style={{ width: "17px" }} src={Shorting} alt="Shorting" />
            </button>
            {isSortDropdownOpen && (
              <div className="absolute z-10 mt-1 w-64 bg-white border border-gray-200 rounded-md shadow-lg py-2">
                {sortOptions.map((option) => (
                  <button
                    key={option.label}
                    className={`block w-full text-left px-4 py-2 hover:bg-[#f9f6ff] ${currentSortOption === option.label ? "bg-[#f9f6ff]" : ""
                      }`}
                    onClick={() => handleTaskSort(option.label)}
                  >
                    <div className="flex justify-between items-center text-sm">
                      <span>{option.label}</span>
                      {currentSortOption === option.label && <span>✓</span>}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-2">
          <button
            onClick={handleShow}
            className="flex text-[#747474] items-center gap-2 border border-[#C9C9C9] bg-white px-4 py-2 rounded-[10px] hover:bg-gray-100 text-[13px] font-medium"
          >
            <img style={{ width: "15px" }} src={filtericon} alt="filtericon" />
            <span>Filter</span>
          </button>
          <button
            onClick={() => setOpenActivity(true)}
            className="flex text-black text-xs font-medium items-center gap-2 bg-[#F9F6FF] px-4 py-2 rounded-[10px]"
          >
            <PlusCircle size={20} />
            <span>Add Activity</span>
          </button>
          <ModalMeet
            isOpen={isOpenActivity}
            onClose={() => setOpenActivity(false)}
            widthClass="max-w-[600px]"
          >
            <LogActivityModal onClose={() => setOpenActivity(false)} />
          </ModalMeet>
          <button
            onClick={() => setOpenNewTask(true)}
            className="flex text-white text-xs font-medium items-center gap-2 bg-[#7E57C2] px-4 py-2 rounded-[10px]"
          >
            <PlusCircle size={20} color={"white"} />
            <span>New Task</span>
          </button>
          <ModalMeet
            isOpen={isOpenNewTask}
            onClose={() => setOpenNewTask(false)}
            widthClass="max-w-[600px]"
          >
            <NewTask onClose={() => setOpenNewTask(false)} />
          </ModalMeet>
        </div>
      </div>
      <TasksPageTable
        {...{
          tasks: sortedTasks,
          avatars,
          displayTaskCount: true,
          page: currentPage,
          isLoading,
          pageSize,
          totalCount: totalTasks,
          onPageChange,
          setPage,
          setPageSize,
          handleSort: handleTaskSort,
        }}
      />
      <Offcanvas
        show={show && !feedbackOpen}
        onHide={handleClose}
        placement="end"
        className="my_offcanvas"
        backdrop={false}
        keyboard={true}
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>Filters</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="offcanvas-body-scroll"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          onWheel={(e) => e.stopPropagation()}
        >
          <div className="h-full">
            <div className="relative custom_input_flled">
              <label>Search</label>
              <input
                type="search"
                className="form-control"
                placeholder="Search..."
                value={tempSearchTerm}
                onChange={(e) => {
                  const value = e.target.value;
                  setTempSearchTerm(value);
                  if (isNumericSearch(value)) {
                    setSearchTerm("");
                    return;
                  }
                  setSearchTerm(value);
                }}
              />
            </div>
            {/* Assignees Dropdown */}
            <div
              className="relative custom_input_flled"
              ref={assigneeDropdownRef}
            >
              <label>Assignees</label>
              <div
                onClick={() => setAssigneeDropdownOpen(!assigneeDropdownOpen)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setAssigneeDropdownOpen(!assigneeDropdownOpen);
                  }
                }}
                className="form-control assignee-div"
                role="button"
                tabIndex={0}
                aria-haspopup="listbox"
                aria-expanded={assigneeDropdownOpen ? "true" : "false"}
              >
                {tempAssigneeFilter.length > 0 ? (
                  tempAssigneeFilter.map((selected) => {
                    const assignee = assigneeOptions.find(
                      (a) => a.value === selected.value
                    );
                    // Use label from state if assignee not found in options (fallback for unloaded/hidden users)
                    const label = assignee ? assignee.label : selected.label;
                    return (
                      <span
                        key={selected.value}
                        className="assignee-span d-inline-flex align-items-center gap-1"
                      >
                        <span
                          style={{ padding: "3px", paddingLeft: "6px" }}
                          className="participant-text"
                        >
                          {label}
                        </span>
                        <button
                          type="button"
                          className="assignee-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleAssignee(selected.value);
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = "#FFBDAD";
                            e.currentTarget.style.color = "#DE350B";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor =
                              "transparent";
                            e.currentTarget.style.color = "#6c757d";
                          }}
                          aria-label={`Remove ${label}`}
                        >
                          <svg height="14" width="14" viewBox="0 0 20 20" aria-hidden="true" focusable="false" className="css-tj5bde-Svg">
                            <path d="M14.348 14.849c-0.469 0.469-1.229 0.469-1.697 0l-2.651-3.030-2.651 3.029c-0.469 0.469-1.229 0.469-1.697 0-0.469-0.469-0.469-1.229 0-1.697l2.758-3.15-2.759-3.152c-0.469-0.469-0.469-1.228 0-1.697s1.228-0.469 1.697 0l2.652 3.031 2.651-3.031c0.469-0.469 1.228-0.469 1.697 0s0.469 1.229 0 1.697l-2.758 3.152 2.758 3.15c0.469 0.469 0.469 1.229 0 1.698z"></path>
                          </svg>
                        </button>
                      </span>
                    );
                  })
                ) : (
                  <span className="text-gray-500">Select assignee(s)</span>
                )}
              </div>
              {assigneeDropdownOpen && (
                <div
                  className="check_dropdown"
                  ref={assigneeDropdownListRef}
                  onKeyDown={handleAssigneeKeyDown}
                  tabIndex={-1}
                >
                  <div className="custom_input_flled mb-0 w-100">
                    <input
                      ref={assigneeSearchInputRef}
                      type="text"
                      placeholder="Search assignees..."
                      value={assigneeSearchTerm}
                      onChange={(e) => setAssigneeSearchTerm(e.target.value)}
                      onKeyDown={handleAssigneeKeyDown}
                      className="w-100"
                    />
                  </div>
                  {filteredAssigneeOptions.length > 0 ? (
                    filteredAssigneeOptions.map((assignee, index) => (
                      <div
                        ref={(el) => {
                          assigneeOptionRefs.current[index] = el;
                        }}
                        className={`check_dropdown_list ${focusedAssigneeIndex === index ? "focused" : ""
                          }`}
                        key={assignee.value}
                        onMouseEnter={() => setFocusedAssigneeIndex(index)}
                      >
                        <input
                          id={`assignee-${assignee.value}`}
                          type="checkbox"
                          checked={tempAssigneeFilter.some(
                            (f) => f.value === assignee.value
                          )}
                          onChange={() => toggleAssignee(assignee.value)}
                        />
                        <label htmlFor={`assignee-${assignee.value}`}>
                          {assignee.label}
                        </label>
                      </div>
                    ))
                  ) : (
                    <div className="stats">
                      <p>No assignees found</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Participants Dropdown */}
            <div
              className="relative custom_input_flled"
              ref={participantDropdownRef}
            >
              <label>Participants</label>
              <div
                onClick={() =>
                  setParticipantDropdownOpen(!participantDropdownOpen)
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setParticipantDropdownOpen(!participantDropdownOpen);
                  }
                }}
                className="form-control assignee-div"
                role="button"
                tabIndex={0}
                aria-haspopup="listbox"
                aria-expanded={participantDropdownOpen ? "true" : "false"}
              >
                {tempParticipantFilter.length > 0 ? (
                  tempParticipantFilter.map((selected) => {
                    const participant = participantOptions.find(
                      (p) => p.value === selected.value
                    );
                    const label = participant ? participant.label : selected.label;
                    return (
                      <span
                        key={selected.value}
                        className="participant-span d-inline-flex align-items-center gap-1"
                      >
                        <span
                          style={{ padding: "3px", paddingLeft: "6px" }}
                          className="participant-text"
                        >
                          {label}
                        </span>
                        <button
                          type="button"
                          className="participant-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleParticipant(selected.value);
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = "#FFBDAD";
                            e.currentTarget.style.color = "#DE350B";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor =
                              "transparent";
                            e.currentTarget.style.color = "#6c757d";
                          }}
                          aria-label={`Remove ${label}`}
                        >
                          <svg height="14" width="14" viewBox="0 0 20 20" aria-hidden="true" focusable="false" className="css-tj5bde-Svg">
                            <path d="M14.348 14.849c-0.469 0.469-1.229 0.469-1.697 0l-2.651-3.030-2.651 3.029c-0.469 0.469-1.229 0.469-1.697 0-0.469-0.469-0.469-1.229 0-1.697l2.758-3.15-2.759-3.152c-0.469-0.469-0.469-1.228 0-1.697s1.228-0.469 1.697 0l2.652 3.031 2.651-3.031c0.469-0.469 1.228-0.469 1.697 0s0.469 1.229 0 1.697l-2.758 3.152 2.758 3.15c0.469 0.469 0.469 1.229 0 1.698z"></path>
                          </svg>
                        </button>
                      </span>
                    );
                  })
                ) : (
                  <span className="text-gray-500">Select participant(s)</span>
                )}
              </div>
              {participantDropdownOpen && (
                <div
                  className="check_dropdown"
                  ref={participantDropdownListRef}
                  onKeyDown={handleParticipantKeyDown}
                  tabIndex={-1}
                >
                  <div className="custom_input_flled mb-0">
                    <input
                      ref={participantSearchInputRef}
                      type="text"
                      placeholder="Search participants..."
                      value={participantSearchTerm}
                      onChange={(e) => setParticipantSearchTerm(e.target.value)}
                      onKeyDown={handleParticipantKeyDown}
                    />
                  </div>
                  {filteredParticipantOptions.length > 0 ? (
                    filteredParticipantOptions.map((participant, index) => (
                      <div
                        ref={(el) => {
                          participantOptionRefs.current[index] = el;
                        }}
                        className={`check_dropdown_list ${focusedParticipantIndex === index ? "focused" : ""
                          }`}
                        key={participant.value}
                        onMouseEnter={() => setFocusedParticipantIndex(index)}
                      >
                        <input
                          id={`participant-${participant.value}`}
                          type="checkbox"
                          checked={tempParticipantFilter.some(
                            (f) => f.value === participant.value
                          )}
                          onChange={() => toggleParticipant(participant.value)}
                        />
                        <label htmlFor={`participant-${participant.value}`}>
                          {participant.label}
                        </label>
                      </div>
                    ))
                  ) : (
                    <div className="stats">
                      <p>No participants found</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="custom_input_flled">
              <label>Task Type</label>
              <TagSelector
                options={typeOptions}
                value={taskTypeValue}
                onChange={handleTaskTypeChange}
                placeholder="Select task type"
                name="Type"
              />
            </div>

            <div className="custom_input_flled">
              <label>Task Status</label>
              <TagSelector
                options={statusOptions}
                value={taskStatusValue}
                onChange={handleTaskStatusChange}
                placeholder="Select task status"
                name="Status"
              />
            </div>

            <div className="custom_input_flled">
              <label>Task Relative Dates</label>
              <TagSelector
                options={dateOptions}
                value={taskDateValue}
                onChange={handleTaskDateChange}
                placeholder="Select relative dates"
                name="Dates"
              />
            </div>

            <div className="custom_input_flled">
              <label>Tags</label>
              <TagSelector
                options={tagsOptions}
                value={taskTagsValue}
                onChange={handleTaskTagsChange}
                placeholder="Select tags"
                name="Tags"
              />
            </div>
          </div>
        </Offcanvas.Body>
        <div className="offcanvas_form_btn_flx border-top border-gray-200 pt-3">
          <Button variant="secondary" onClick={clearFilters}>
            Clear
          </Button>
          <Button variant="primary" onClick={applyFilters}>
            Apply Filter
          </Button>
        </div>
      </Offcanvas>
    </div>
  );
};

export default TasksPageNew;